/**
 * Responsibility: point the camera from a train, either from the cab or from outside it.
 *
 * ## Why this is not the chase camera
 *
 * Three reasons, and the first one is a bug rather than a preference.
 *
 * `createChaseCamera` lifts itself clear of the ground — `sampleGround(x, z) + MIN_GROUND_CLEARANCE` — which
 * is exactly right for an aircraft and exactly wrong here. **The cab view is deliberately below the
 * surrounding ground** whenever the line runs through a cutting or a gorge, and a camera that refused to go
 * below it would climb out of every cutting in the world, which is the one view this mode exists for.
 *
 * Second, the two use opposite heading conventions: the chase camera takes heading 0 as facing −Z, while a
 * surveyed station advances along `+X` at heading 0. Third, the chase camera has no notion of looking
 * sideways, and looking around is half the control surface of a train.
 *
 * So this is its own thing, and it is much simpler: there is no aim lead, no ground lift, and no roll
 * easing, because the track already knows where it is going.
 *
 * ## Both views are damped, for different reasons
 *
 * The outside view lags the train so a curve reads as weight rather than as the camera being welded on. The
 * cab view does not lag in position — you are sitting in it — but its *look* direction eases, because
 * turning your head is not instant either. Nothing in this project responds immediately.
 */

import * as THREE from 'three';

import { damp } from '../core/damp.js';
import { RAILWAY, degToRad } from '../config.js';

/** The two ways of watching a train. */
export const TRAIN_VIEWS = Object.freeze(['cab', 'outside']);

/**
 * Create the train camera controller.
 *
 * @param {THREE.Camera} camera
 * @param {object} [options]
 * @param {string} [options.view] - `'cab'` or `'outside'`.
 * @param {object} [options.tuning] - Overrides for the `RAILWAY` config block.
 * @returns {object}
 */
export function createTrainCamera(camera, { view = 'cab', tuning: overrides = RAILWAY } = {}) {
  if (!camera || !camera.isCamera) {
    throw new Error('createTrainCamera: a Three.js camera is required');
  }

  const tuning = overrides === RAILWAY ? RAILWAY : { ...RAILWAY, ...overrides };

  let current = TRAIN_VIEWS.includes(view) ? view : 'cab';

  /*
    Reused between frames. Allocating vectors per frame is the classic way to make a render loop
    garbage-collect visibly, and this runs sixty times a second forever.
  */
  const eye = new THREE.Vector3();
  const target = new THREE.Vector3();
  const settled = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);

  let hasSettled = false;

  /**
   * Where the eye wants to be for a pose.
   *
   * The cab sits on the railhead, a head's height above it. Outside sits behind and above, along the track's
   * own heading — behind in the sense of the direction of travel, so reversing swaps which end you see.
   */
  function idealEyeFor(pose) {
    if (current === 'cab') {
      eye.set(pose.x, pose.y + tuning.RAIL_LIFT + tuning.CAB_EYE_HEIGHT, pose.z);
      return eye;
    }

    eye.set(
      pose.x - Math.cos(pose.heading) * tuning.CHASE_BEHIND,
      pose.y + tuning.CHASE_ABOVE,
      pose.z - Math.sin(pose.heading) * tuning.CHASE_BEHIND,
    );

    return eye;
  }

  /**
   * Where the camera looks, given the pose and how far the rider has turned their head.
   *
   * The look offset is applied to the *aim* rather than to the eye, so swinging your view does not move you.
   * From outside it is applied too, which reads as the camera swinging around the train.
   */
  function aimFor(pose, from) {
    const heading = pose.heading + (pose.look ?? 0);
    const reach = tuning.CAB_AIM_AHEAD;

    target.set(
      pose.x + Math.cos(heading) * reach,
      pose.y + tuning.RAIL_LIFT + tuning.CAB_EYE_HEIGHT,
      pose.z + Math.sin(heading) * reach,
    );

    /*
      From outside, the aim is the train itself rather than the far distance, or the camera would look past
      what it is meant to be showing.
    */
    if (current === 'outside') {
      target.lerpVectors(target, new THREE.Vector3(pose.x, pose.y + tuning.CHASE_ABOVE * 0.35, pose.z), 0.72);
    }

    void from;
    return target;
  }

  /** Apply cant, so the horizon leans into a curve. */
  function applyRoll(pose) {
    up.set(0, 1, 0).applyAxisAngle(
      camera.getWorldDirection(new THREE.Vector3()),
      -(pose.roll ?? 0),
    );
    camera.up.copy(up);
  }

  return {
    /**
     * Move the camera toward where the pose says it should be.
     *
     * @param {object} pose - From `trainModel.poseFor`: `{x, y, z, heading, roll, look}`.
     * @param {number} dt - Seconds since the last frame.
     */
    update(pose, dt) {
      const ideal = idealEyeFor(pose);

      if (!hasSettled) {
        settled.copy(ideal);
        hasSettled = true;
      } else if (current === 'outside') {
        // Only the outside view lags. You do not lag behind the seat you are sitting in.
        settled.set(
          damp(settled.x, ideal.x, dt, tuning.CHASE_TIME_CONSTANT),
          damp(settled.y, ideal.y, dt, tuning.CHASE_TIME_CONSTANT),
          damp(settled.z, ideal.z, dt, tuning.CHASE_TIME_CONSTANT),
        );
      } else {
        settled.copy(ideal);
      }

      camera.position.copy(settled);

      const aim = aimFor(pose, settled);
      camera.up.set(0, 1, 0);
      camera.lookAt(aim);
      applyRoll(pose);
    },

    /** Put the camera exactly where it belongs, with no easing. For entering a world. */
    snapTo(pose) {
      hasSettled = false;
      this.update(pose, 0);
    },

    /** Switch view, easing in from wherever the other one left off. */
    setView(next) {
      if (!TRAIN_VIEWS.includes(next) || next === current) return current;
      current = next;

      // Not snapped: letting the outside view ease in from the cab position reads as pulling back rather than a cut.
      return current;
    },

    /** Swap to whichever view is not showing. */
    toggleView() {
      return this.setView(current === 'cab' ? 'outside' : 'cab');
    },

    getView() {
      return current;
    },

    /** Reset the camera's roll convention, for handing control back to the aircraft. */
    release() {
      camera.up.set(0, 1, 0);
      hasSettled = false;
    },
  };
}
